import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import MainLayout from '@components/layout/MainLayout';
import Spinner from '@components/common/Spinner';
import { STORAGE_KEYS } from '@constants/index';

import { OnboardingRoute, ProtectedRoute, PublicOnlyRoute } from './guards';
import { PATHS } from './paths';

const Dashboard = lazy(() => import('@pages/Dashboard'));
const Aliases = lazy(() => import('@pages/Aliases'));
const AliasDetail = lazy(() => import('@pages/AliasDetail'));
const Activity = lazy(() => import('@pages/Activity'));
const Senders = lazy(() => import('@pages/Senders'));
const Billing = lazy(() => import('@pages/Billing'));
const Settings = lazy(() => import('@pages/Settings'));

const Login = lazy(() => import('@pages/Auth/Login'));
const Signup = lazy(() => import('@pages/Auth/Signup'));
const ForgotPassword = lazy(() => import('@pages/Auth/ForgotPassword'));
const CheckEmail = lazy(() => import('@pages/Auth/CheckEmail'));
const VerifyEmail = lazy(() => import('@pages/Auth/VerifyEmail'));
const ResetPassword = lazy(() => import('@pages/Auth/ResetPassword'));
const Onboarding = lazy(() => import('@pages/Auth/Onboarding'));
const NotFound = lazy(() => import('@pages/NotFound'));

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export default function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<PublicOnlyRoute />}>
          <Route path={PATHS.login} element={<Login />} />
          <Route path={PATHS.signup} element={<Signup />} />
          <Route path={PATHS.forgotPassword} element={<ForgotPassword />} />
        </Route>

        {/* Visitable in any session state (inbox links, in-flight reset). */}
        <Route path={PATHS.checkEmail} element={<CheckEmail />} />
        <Route path={PATHS.verifyEmail} element={<VerifyEmail />} />
        <Route path={PATHS.resetPassword} element={<ResetPassword />} />

        <Route element={<OnboardingRoute />}>
          <Route path={PATHS.onboarding} element={<Onboarding />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route path={PATHS.dashboard} element={<Dashboard />} />
            <Route path={PATHS.aliases} element={<Aliases />} />
            <Route path={PATHS.aliasDetail} element={<AliasDetail />} />
            <Route path={PATHS.activity} element={<Activity />} />
            <Route path={PATHS.senders} element={<Senders />} />
            <Route path={PATHS.billing} element={<Billing />} />
            <Route path={PATHS.settings} element={<Settings />} />
          </Route>
        </Route>

        <Route path={PATHS.notFound} element={<NotFoundOrLogin />} />
      </Routes>
    </Suspense>
  );
}

function NotFoundOrLogin() {
  if (typeof window !== 'undefined' && !localStorage.getItem(STORAGE_KEYS.authToken)) {
    return <Navigate to={PATHS.login} replace />;
  }
  return <NotFound />;
}
